import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { datasetsAPI, API_BASE } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Upload, Database, BarChart3, TableProperties, Trash2, FileSpreadsheet,
  Users, TestTube, Pill, AlertTriangle, CheckCircle, ChevronLeft
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

// Try importing Plotly
let Plot;
try {
  Plot = require('react-plotly.js').default;
} catch {
  Plot = null;
}

const ROLE_COLORS = {
  id: 'text-blue-400 bg-blue-400/10',
  time: 'text-green-400 bg-green-400/10',
  conc: 'text-orange-400 bg-orange-400/10',
  dose: 'text-purple-400 bg-purple-400/10',
  evid: 'text-pink-400 bg-pink-400/10',
  weight: 'text-cyan-400 bg-cyan-400/10',
  age: 'text-amber-400 bg-amber-400/10',
  sex: 'text-rose-400 bg-rose-400/10',
  other: 'text-muted-foreground bg-muted/30',
};

function UploadZone({ projectId, onUploaded }) {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setIsDragging(true);
    else if (e.type === 'dragleave') setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) uploadFile(file);
  }, [projectId]);

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('project_id', projectId);
    setUploading(true);
    setProgress(0);

    try {
      const res = await datasetsAPI.upload(formData);
      setProgress(100);
      toast.success(`Uploaded: ${res.data.filename} (${res.data.n_observations} rows, ${res.data.n_subjects} subjects)`);
      onUploaded?.(res.data);
    } catch (err) {
      toast.error('Upload failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  return (
    <div
      data-testid="dataset-upload-dropzone"
      onDragEnter={handleDrag}
      onDragOver={handleDrag}
      onDragLeave={handleDrag}
      onDrop={handleDrop}
      onClick={() => fileRef.current?.click()}
      className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-200 ${
        isDragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-muted/20'
      }`}
    >
      <input ref={fileRef} type="file" accept=".csv,.tsv,.xpt,.xlsx,.sas7bdat" onChange={(e) => { if (e.target.files?.[0]) uploadFile(e.target.files[0]); e.target.value = ''; }} className="hidden" />
      {uploading ? (
        <div className="space-y-3">
          <div className="w-10 h-10 mx-auto rounded-lg bg-primary/15 flex items-center justify-center">
            <Upload className="w-5 h-5 text-primary animate-pulse" />
          </div>
          <p className="text-sm text-muted-foreground">Uploading...</p>
          <div className="w-48 h-1.5 bg-muted rounded-full mx-auto overflow-hidden">
            <motion.div className="h-full bg-primary rounded-full" initial={{ width: 0 }} animate={{ width: `${progress}%` }} />
          </div>
        </div>
      ) : (
        <>
          <Upload className={`w-8 h-8 mx-auto mb-3 ${isDragging ? 'text-primary' : 'text-muted-foreground/50'}`} />
          <p className="text-sm font-medium">Drop a PK dataset here</p>
          <p className="text-xs text-muted-foreground mt-1">CSV, XPT, SAS7BDAT, XLSX</p>
        </>
      )}
    </div>
  );
}

function DataProfileCard({ dataset }) {
  const meta = dataset.metadata_json || {};
  const profile = meta.profile || {};
  const cols = meta.columns || [];
  const pkCols = profile.pk_columns || [];
  const doselevels = meta.dose_levels || [];

  return (
    <Card className="card-hover">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
          <FileSpreadsheet className="w-4 h-4 text-primary" />
          {dataset.filename}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Stats grid */}
        <div className="grid grid-cols-4 gap-3">
          <div className="p-3 rounded-lg bg-muted/30 text-center">
            <Users className="w-4 h-4 mx-auto text-blue-400 mb-1" />
            <p className="text-lg font-bold font-mono">{dataset.n_subjects}</p>
            <p className="text-[10px] text-muted-foreground">Subjects</p>
          </div>
          <div className="p-3 rounded-lg bg-muted/30 text-center">
            <Database className="w-4 h-4 mx-auto text-green-400 mb-1" />
            <p className="text-lg font-bold font-mono">{(dataset.n_observations || 0).toLocaleString()}</p>
            <p className="text-[10px] text-muted-foreground">Observations</p>
          </div>
          <div className="p-3 rounded-lg bg-muted/30 text-center">
            <TableProperties className="w-4 h-4 mx-auto text-orange-400 mb-1" />
            <p className="text-lg font-bold font-mono">{dataset.n_cols}</p>
            <p className="text-[10px] text-muted-foreground">Columns</p>
          </div>
          <div className="p-3 rounded-lg bg-muted/30 text-center">
            <Pill className="w-4 h-4 mx-auto text-purple-400 mb-1" />
            <p className="text-lg font-bold font-mono">{doselevels.length}</p>
            <p className="text-[10px] text-muted-foreground">Dose Levels</p>
          </div>
        </div>

        {/* Dose pills */}
        {doselevels.length > 0 && (
          <div>
            <p className="text-xs text-muted-foreground mb-1.5">Dose Levels</p>
            <div className="flex flex-wrap gap-1.5">
              {doselevels.map(d => (
                <span key={d} className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-400">
                  {d}mg
                </span>
              ))}
            </div>
          </div>
        )}

        {/* PK columns */}
        {pkCols.length > 0 && (
          <div>
            <p className="text-xs text-muted-foreground mb-1.5">Detected PK Columns</p>
            <div className="flex flex-wrap gap-1.5">
              {pkCols.map(c => (
                <span key={c} className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-primary/10 text-primary">{c}</span>
              ))}
            </div>
          </div>
        )}

        {/* Format badge */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="font-mono px-2 py-0.5 rounded bg-muted">{dataset.format?.toUpperCase()}</span>
          <span>{(dataset.file_size / 1024).toFixed(1)} KB</span>
          <span>{new Date(dataset.uploaded_at).toLocaleDateString()}</span>
        </div>
      </CardContent>
    </Card>
  );
}

function DataTableView({ datasetId }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadPreview(); }, [datasetId]);

  const loadPreview = async () => {
    try {
      const res = await axios.get(`${API_BASE}/datasets/${datasetId}/preview?rows=100`);
      setPreview(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  if (loading) return <div className="space-y-2 p-4">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-8" />)}</div>;
  if (!preview) return <p className="text-sm text-muted-foreground p-4">No preview available</p>;

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            {preview.columns.map((col, i) => (
              <TableHead key={i} className="whitespace-nowrap">
                <div className="flex items-center gap-1.5">
                  <span className={`text-[9px] font-mono px-1 py-0.5 rounded ${ROLE_COLORS[col.role] || ROLE_COLORS.other}`}>
                    {col.role !== 'other' ? col.role.toUpperCase() : ''}
                  </span>
                  <span className="text-xs font-mono">{col.name}</span>
                </div>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {preview.rows.map((row, ri) => (
            <TableRow key={ri}>
              {row.map((cell, ci) => (
                <TableCell key={ci} className="text-xs font-mono tabular-nums whitespace-nowrap py-1.5">
                  {cell}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className="p-3 text-xs text-muted-foreground text-center border-t border-border">
        Showing {preview.preview_rows} of {preview.total_rows.toLocaleString()} rows
      </div>
    </div>
  );
}

function SpaghettiPlotView({ datasetId }) {
  const [plotData, setPlotData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logScale, setLogScale] = useState(true);

  useEffect(() => { loadPlot(); }, [datasetId, logScale]);

  const loadPlot = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/datasets/${datasetId}/spaghetti?log_scale=${logScale}`);
      setPlotData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  if (loading) return <Skeleton className="h-64 rounded-xl" />;
  if (!plotData || !Plot) return <p className="text-sm text-muted-foreground p-4">Plot not available</p>;

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Concentration-Time Profile</span>
        <div className="flex items-center gap-2">
          <Label className="text-xs">Log scale</Label>
          <Switch checked={logScale} onCheckedChange={setLogScale} />
        </div>
      </div>
      <div className="rounded-xl border border-border overflow-hidden" style={{ height: 350 }}>
        <Plot
          data={plotData.plotly_data}
          layout={{
            ...plotData.plotly_layout,
            yaxis: { ...plotData.plotly_layout.yaxis, type: logScale ? 'log' : 'linear' },
            autosize: true,
          }}
          config={{ displayModeBar: true, responsive: true }}
          style={{ width: '100%', height: '100%' }}
          useResizeHandler={true}
        />
      </div>
    </div>
  );
}

function CovariatePlotsView({ datasetId }) {
  const [plots, setPlots] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, [datasetId]);

  const load = async () => {
    try {
      const res = await axios.get(`${API_BASE}/datasets/${datasetId}/covariates`);
      setPlots(res.data.plots || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  if (loading) return <div className="grid grid-cols-2 gap-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-40 rounded-xl" />)}</div>;
  if (!plots.length || !Plot) return <p className="text-sm text-muted-foreground">No covariate plots available</p>;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {plots.map((p, i) => (
        <div key={i} className="rounded-xl border border-border overflow-hidden" style={{ height: 220 }}>
          <Plot
            data={p.plotly_data}
            layout={{ ...p.plotly_layout, autosize: true }}
            config={{ displayModeBar: false, responsive: true }}
            style={{ width: '100%', height: '100%' }}
            useResizeHandler={true}
          />
        </div>
      ))}
    </div>
  );
}

export default function DataExplorer({ projectId, onUploadComplete }) {
  const [datasets, setDatasets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [activeView, setActiveView] = useState('profile');

  useEffect(() => { loadData(); }, [projectId]);

  const loadData = async () => {
    try {
      const res = await datasetsAPI.listByProject(projectId);
      const list = res.data.datasets || [];
      setDatasets(list);
      if (list.length > 0 && !selected) setSelected(list[0]);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleUploaded = (dataset) => {
    setDatasets(prev => [dataset, ...prev]);
    setSelected(dataset);
    onUploadComplete?.(dataset);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this dataset?')) return;
    try {
      await axios.delete(`${API_BASE}/datasets/${id}`);
      setDatasets(prev => prev.filter(d => d.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success('Dataset deleted');
    } catch (e) { toast.error('Failed to delete'); }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <h2 className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>
          {selected ? (
            <button onClick={() => setSelected(null)} className="flex items-center gap-2 hover:text-primary transition-colors">
              <ChevronLeft className="w-4 h-4" /> {selected.filename}
            </button>
          ) : 'Data Explorer'}
        </h2>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4">
          {!selected ? (
            <>
              {/* Upload zone */}
              <UploadZone projectId={projectId} onUploaded={handleUploaded} />

              {/* Dataset list */}
              {loading ? (
                <div className="space-y-3">{[1,2].map(i => <Skeleton key={i} className="h-20 rounded-xl" />)}</div>
              ) : datasets.length === 0 ? (
                <div className="text-center py-8 text-sm text-muted-foreground">
                  No datasets uploaded yet
                </div>
              ) : (
                datasets.map((ds, i) => (
                  <motion.div key={ds.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                    <Card className="card-hover cursor-pointer" onClick={() => setSelected(ds)}>
                      <CardContent className="py-3 px-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-sm font-mono">{ds.filename}</p>
                            <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                              <span>{ds.n_observations?.toLocaleString()} rows</span>
                              <span>{ds.n_subjects} subjects</span>
                              <span className="font-mono px-1.5 py-0.5 rounded bg-muted">{ds.format?.toUpperCase()}</span>
                            </div>
                          </div>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); handleDelete(ds.id); }}>
                            <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))
              )}
            </>
          ) : (
            <>
              {/* Profile card */}
              <DataProfileCard dataset={selected} />

              {/* Tabbed views */}
              <Tabs value={activeView} onValueChange={setActiveView}>
                <TabsList className="bg-muted/30 p-1">
                  <TabsTrigger value="profile" className="text-xs">
                    <BarChart3 className="w-3.5 h-3.5 mr-1" /> Spaghetti Plot
                  </TabsTrigger>
                  <TabsTrigger value="table" className="text-xs">
                    <TableProperties className="w-3.5 h-3.5 mr-1" /> Data Table
                  </TabsTrigger>
                  <TabsTrigger value="covariates" className="text-xs">
                    <BarChart3 className="w-3.5 h-3.5 mr-1" /> Covariates
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="profile" className="mt-4">
                  <SpaghettiPlotView datasetId={selected.id} />
                </TabsContent>

                <TabsContent value="table" className="mt-4">
                  <div className="rounded-xl border border-border overflow-hidden">
                    <DataTableView datasetId={selected.id} />
                  </div>
                </TabsContent>

                <TabsContent value="covariates" className="mt-4">
                  <CovariatePlotsView datasetId={selected.id} />
                </TabsContent>
              </Tabs>
            </>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
